import * as React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Inbox, type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface Crumb {
  label: string;
  to?: string;
}

export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  className,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  breadcrumbs?: Crumb[];
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    // mb-7: the page title needs distinctly more air beneath it than the filter bar
    // needs above the table, or the three bands read as one undifferentiated stack.
    <div className={cn('mb-7 flex flex-col gap-3.5', className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb">
          <ol className="text-muted-soft flex flex-wrap items-center gap-1 text-[13px]">
            {breadcrumbs.map((crumb, index) => (
              <li key={index} className="flex items-center gap-1">
                {index > 0 && <ChevronRight className="size-3.5 opacity-40" />}
                {crumb.to ? (
                  <Link to={crumb.to} className="hover:text-foreground transition-colors duration-150">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-muted-foreground font-medium">{crumb.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="truncate text-[22px] leading-tight font-semibold sm:text-[26px]">{title}</h1>
          {description && (
            <p className="text-muted-soft mt-1.5 text-[13.5px] leading-relaxed">{description}</p>
          )}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

/** Shared empty state for panels that are not tables. */
export function EmptyState({
  icon: Icon = Inbox,
  title,
  hint,
  action,
  className,
  compact,
}: {
  icon?: LucideIcon;
  title: string;
  hint?: string;
  action?: React.ReactNode;
  className?: string;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-1.5 text-center',
        compact ? 'py-9' : 'py-16',
        className,
      )}
    >
      {/* Same soft disc as the table's empty state, so the two read as one idea. */}
      <span
        className={cn(
          'text-muted-soft mb-1 grid place-items-center rounded-full bg-[var(--disc-bg)]',
          compact ? 'size-9' : 'size-11',
        )}
        aria-hidden
      >
        <Icon className={cn(compact ? 'size-4' : 'size-5')} />
      </span>
      <p className="font-medium">{title}</p>
      {hint && <p className="text-muted-soft max-w-sm text-[13px] leading-relaxed">{hint}</p>}
      {action && <div className="mt-2.5">{action}</div>}
    </div>
  );
}

/**
 * Label/value pair — one hairline-separated row, label left, value right.
 *
 * The row rule is the point. An earlier version paired a label to its value with nothing
 * but a gap, in a two-column grid; two things went wrong that a screenshot made obvious.
 * The label column was `minmax(5.5rem, 11.5rem)` and so always took its 184px maximum
 * whenever the workspace had room, which left ~110px of dead space between a short label
 * like «Policy no.» and its value — at that distance the eye stops reading them as a
 * pair. And with two columns, a section holding an odd number of fields ended on a blank
 * cell, which reads as missing data rather than as the end of a list.
 *
 * A hairline under each row fixes both. It ties the label to its value across whatever
 * gap remains, so the pairing survives; it gives the section a regular rhythm to scan
 * instead of a field of floating text; and going to one column makes a blank cell
 * impossible. This is the table Christina asked for, minus the table's chrome — three
 * bordered panels stacked on one record would weigh far more than five hairlines do.
 *
 * `min-h-9` + `items-center` is the other half: a value that is a badge occupies exactly
 * the same row height as a value that is text, so a column containing both stays a
 * regular rhythm instead of jumping around the badges.
 */
export function Field({
  label,
  children,
  className,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
  mono?: boolean;
}) {
  return (
    <div
      className={cn(
        'border-border grid min-h-9 grid-cols-[minmax(0,9.5rem)_minmax(0,1fr)] items-center gap-x-5',
        'border-b py-2 last:border-b-0',
        className,
      )}
    >
      {/*
        The label wraps rather than truncates. Greek labels run long — «Ονοματεπώνυμο /
        Επωνυμία», «Συγκατάθεση επικοινωνίας» — and on a detail page the label is what
        gives the value its meaning, so an ellipsis costs more than an uneven row does.
        At 9.5rem the longest of them take two lines; that is the deliberate trade for
        killing the dead space that 11.5rem opened under every short label, and the row's
        hairline keeps a two-line label legibly attached to its value.
      */}
      <dt className="text-muted-soft text-[12.5px] leading-snug">{label}</dt>
      <dd className={cn('min-w-0 text-[13.5px] leading-snug', mono && 'tabular')}>{children ?? '—'}</dd>
    </div>
  );
}

/**
 * One column of hairline-separated field rows, capped at a readable measure.
 *
 * This was two columns. Dropping to one costs vertical space and buys three things: no
 * section can end on a blank cell, the eye tracks a single label axis per page instead of
 * two that moved as sections changed field count, and a value never sits half a screen
 * from its label. The widest grid in the app is six fields (client identity), so one
 * column is six rows — a length worth scanning, not a scroll.
 *
 * 42rem rather than the old 64rem: with one column the cap is what keeps the row's value
 * from drifting away from its label on a wide monitor. The hairline spans the measure, so
 * the cap is visible rather than implied — the rules stop where the content stops.
 *
 * The column count is fixed here rather than passed in per section — that was exactly
 * the freedom that let four sections on one page each pick a different grid.
 */
export function FieldGrid({ children, className }: { children: React.ReactNode; className?: string }) {
  return <dl className={cn('grid max-w-2xl grid-cols-1 gap-y-0', className)}>{children}</dl>;
}

// BackLink used to sit above every detail page's breadcrumb. It was removed with the
// detail-page pass: the breadcrumb's first crumb already links to the same list, and two
// ways back stacked on top of each other is duplication, not affordance.
